import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { dateFormatter } from 'utils/grid';
import { GridColDef } from '@mui/x-data-grid-pro';
import {
  ExpenseClaimResponse,
  ExpenseClaimRouteResponse
} from 'generated/index';

const useColumnDefinitions = () => {
  const { t } = useTranslation('common');

  return useMemo(
    () =>
      [
        {
          field: 'employmentNumber',
          type: 'string',
          headerName: t('travelExpenseClaims.employmentNumber'),
          valueGetter: (_, row) => row?.employment?.number ?? '',
          width: 150
        },
        {
          field: 'employeeName',
          type: 'string',
          headerName: t('employee'),
          valueGetter: (_, row) => row?.employment?.employeeName ?? '',
          minWidth: 200
        },
        {
          field: 'date',
          type: 'date',
          headerName: t('date'),
          valueFormatter: dateFormatter,
          width: 120
        },
        {
          field: 'routes',
          type: 'string',
          headerName: t('travelExpenseClaims.travelDistance'),
          valueFormatter: (value: ExpenseClaimResponse['routes']) => {
            const routes = (value || []) as ExpenseClaimRouteResponse[];
            const distanceInMeters = routes.reduce(
              (acc, route) => acc + route.distanceInMeters,
              0
            );
            const distanceInKm = distanceInMeters / 1000;
            return distanceInKm % 1 === 0
              ? distanceInKm
              : distanceInKm.toFixed(2).replace('.', ',');
          },
          width: 120
        },
        {
          field: 'transportationTypeName',
          type: 'string',
          headerName: t('transportationType'),
          valueFormatter: (value: ExpenseClaimResponse['transportationType']) =>
            value?.name || '',
          minWidth: 150
        },
        {
          field: 'declarationType',
          type: 'string',
          headerName: t('travelExpenseClaims.declarationType'),
          valueFormatter: (value: ExpenseClaimResponse['declarationType']) =>
            value?.name || '',
          minWidth: 120
        },
        {
          field: 'description',
          type: 'string',
          headerName: t('description'),
          flex: 1,
          minWidth: 200
        },
        {
          field: 'statusNote',
          type: 'string',
          headerName: t('travelExpenseClaims.statusNote'),
          flex: 1,
          minWidth: 200
        },
        {
          field: 'status',
          type: 'string',
          headerName: t('status'),
          valueFormatter: (value: ExpenseClaimResponse['status']) =>
            value ? t(`travelExpenseClaims.status.${value}`) : '',
          minWidth: 150
        }
      ] as GridColDef[],
    [t]
  );
};

export default useColumnDefinitions;
